import React, { useState, useEffect } from "react";
import { Plus, Edit, Save, X, FileText, Eye } from "lucide-react";
import { db } from "../../utils/firebase";
import { doc, setDoc, getDoc } from "firebase/firestore";

const RCNotesTab = ({ organizationId, isPublicView = false }) => {
  const [notes, setNotes] = useState({
    "Quick Glance": {
      insights: [],
    },
    Overview: {
      insights: [],
    },
    Progression: {
      insights: [],
    },
    "Key Factors": {
      insights: [],
    },
    "MAT Analysis": {
      insights: [],
    },
    "Additional Thoughts": {
      insights: [],
    },
  });

  const [editingItem, setEditingItem] = useState(null);
  const [tempValue, setTempValue] = useState("");
  const [newItem, setNewItem] = useState("");
  const [addingItemTo, setAddingItemTo] = useState(null);
  const [addingItemType, setAddingItemType] = useState(null);

  useEffect(() => {
    loadNotes();
  }, [organizationId]);

  const loadNotes = async () => {
    try {
      const notesDoc = doc(
        db,
        `organizations/${organizationId}/rcDataNotes`,
        "notes"
      );
      const docSnapshot = await getDoc(notesDoc);

      if (docSnapshot.exists()) {
        const loadedNotes = docSnapshot.data();
        // Convert old format to new format if needed
        const convertedNotes = {};
        Object.keys(loadedNotes).forEach((section) => {
          convertedNotes[section] = {
            insights: Array.isArray(loadedNotes[section].insights)
              ? loadedNotes[section].insights
              : [],
          };
        });
        setNotes(convertedNotes);
      }
    } catch (error) {
      console.error("Failed to load notes:", error);
    }
  };

  const saveNotes = async (updatedNotes) => {
    // Prevent saves in public view
    if (isPublicView) {
      console.warn("Cannot save notes in public view mode");
      return;
    }

    try {
      const notesDoc = doc(
        db,
        `organizations/${organizationId}/rcDataNotes`,
        "notes"
      );
      await setDoc(notesDoc, updatedNotes);
    } catch (error) {
      console.error("Failed to save notes:", error);
    }
  };

  const handleEditItem = (section, type, itemId, currentText) => {
    // Prevent editing in public view
    if (isPublicView) return;

    setEditingItem({ section, type, itemId });
    setTempValue(currentText);
  };

  const handleSaveEdit = async () => {
    // Prevent editing in public view
    if (isPublicView) return;

    const { section, type, itemId } = editingItem;

    const updatedNotes = {
      ...notes,
      [section]: {
        ...notes[section],
        [type]: notes[section][type].map((item) =>
          item.id === itemId
            ? {
                ...item,
                text: tempValue,
                lastModified: new Date().toISOString(),
              }
            : item
        ),
      },
    };

    setNotes(updatedNotes);
    await saveNotes(updatedNotes);

    setEditingItem(null);
    setTempValue("");
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setTempValue("");
  };

  const addItem = async (section, type) => {
    // Prevent adding in public view
    if (isPublicView) return;

    if (newItem.trim()) {
      const newItemObj = {
        id: Date.now(),
        text: newItem.trim(),
        timestamp: new Date().toISOString(),
      };

      const updatedNotes = {
        ...notes,
        [section]: {
          ...notes[section],
          [type]: [...notes[section][type], newItemObj],
        },
      };

      setNotes(updatedNotes);
      await saveNotes(updatedNotes);

      setNewItem("");
      setAddingItemTo(null);
      setAddingItemType(null);
    }
  };

  const removeItem = async (section, type, itemId) => {
    // Prevent removal in public view
    if (isPublicView) return;

    const updatedNotes = {
      ...notes,
      [section]: {
        ...notes[section],
        [type]: notes[section][type].filter((item) => item.id !== itemId),
      },
    };

    setNotes(updatedNotes);
    await saveNotes(updatedNotes);
  };

  const renderItemSection = (sectionName, type, title, color, placeholder) => {
    const items = notes[sectionName][type] || [];
    const isAdding = addingItemTo === sectionName && addingItemType === type;

    return (
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-white font-semibold">{title}</h4>
          {/* Hide add button in public view */}
          {!isPublicView && (
            <button
              onClick={() => {
                setAddingItemTo(sectionName);
                setAddingItemType(type);
              }}
              className={`flex items-center gap-1 px-3 py-1 bg-${color}-500/20 hover:bg-${color}-500/30 rounded-lg text-${color}-400 text-sm transition-colors`}
            >
              <Plus size={14} />
              Add {title.split(" ")[1] || title}
            </button>
          )}
          {/* Show read-only indicator in public view */}
          {isPublicView && (
            <div className="flex items-center gap-1 px-3 py-1 bg-slate-600/20 rounded-lg text-slate-400 text-sm">
              <Eye size={14} />
              View Only
            </div>
          )}
        </div>

        {/* Hide add form in public view */}
        {isAdding && !isPublicView && (
          <div className="mb-3 p-3 bg-slate-900/50 rounded-lg">
            <textarea
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              placeholder={placeholder}
              className="w-full bg-slate-800 text-white p-2 rounded border border-slate-600 focus:border-pink-400 focus:outline-none resize-none"
              rows={4}
            />
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => addItem(sectionName, type)}
                className="flex items-center gap-1 px-3 py-1 bg-green-500/20 hover:bg-green-500/30 rounded text-green-400 text-sm"
              >
                <Save size={14} />
                Save
              </button>
              <button
                onClick={() => {
                  setAddingItemTo(null);
                  setAddingItemType(null);
                  setNewItem("");
                }}
                className="flex items-center gap-1 px-3 py-1 bg-slate-500/20 hover:bg-slate-500/30 rounded text-slate-400 text-sm"
              >
                <X size={14} />
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {items.length === 0 ? (
            <p className="text-slate-400 text-sm italic">
              {isPublicView
                ? `No ${type} available to view.`
                : `No ${type} added yet. Click "Add ${
                    title.split(" ")[1] || title
                  }" to get started.`}
            </p>
          ) : (
            items.map((item) => (
              <div
                key={item.id}
                className="flex items-start gap-3 p-3 bg-slate-900/30 rounded-lg group"
              >
                <div
                  className={`w-2 h-2 bg-${color}-400 rounded-full mt-2 flex-shrink-0`}
                ></div>
                <div className="flex-1">
                  {editingItem &&
                  editingItem.section === sectionName &&
                  editingItem.type === type &&
                  editingItem.itemId === item.id &&
                  !isPublicView ? (
                    <div className="space-y-2">
                      <textarea
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                        className="w-full bg-slate-800 text-white p-2 rounded border border-slate-600 focus:border-pink-400 focus:outline-none resize-none"
                        rows={4}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveEdit}
                          className="flex items-center gap-1 px-2 py-1 bg-green-500/20 hover:bg-green-500/30 rounded text-green-400 text-xs"
                        >
                          <Save size={12} />
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="flex items-center gap-1 px-2 py-1 bg-slate-500/20 hover:bg-slate-500/30 rounded text-slate-400 text-xs"
                        >
                          <X size={12} />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p
                        className={`text-slate-200 text-sm ${
                          !isPublicView ? "cursor-pointer hover:text-white" : ""
                        } transition-colors`}
                        onClick={() =>
                          !isPublicView &&
                          handleEditItem(sectionName, type, item.id, item.text)
                        }
                      >
                        {item.text}
                      </p>
                      <p className="text-slate-500 text-xs mt-1">
                        Added {new Date(item.timestamp).toLocaleDateString()} at{" "}
                        {new Date(item.timestamp).toLocaleTimeString()}
                        {item.lastModified && (
                          <span>
                            {" "}
                            • Modified{" "}
                            {new Date(item.lastModified).toLocaleDateString()}
                          </span>
                        )}
                        {item.author && ` by ${item.author}`}
                      </p>
                    </>
                  )}
                </div>
                {/* Hide action buttons in public view */}
                {!isPublicView && (
                  <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-all">
                    <button
                      onClick={() =>
                        handleEditItem(sectionName, type, item.id, item.text)
                      }
                      className="p-1 hover:bg-blue-500/20 rounded text-blue-400 transition-all"
                    >
                      <Edit size={12} />
                    </button>
                    <button
                      onClick={() => removeItem(sectionName, type, item.id)}
                      className="p-1 hover:bg-red-500/20 rounded text-red-400 transition-all"
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  const renderSection = (sectionName) => {
    return (
      <div
        key={sectionName}
        className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-pink-500/20 rounded-lg">
            <FileText className="text-pink-400" size={24} />
          </div>
          <h3 className="text-xl font-bold text-white">{sectionName}</h3>
          {isPublicView && (
            <span className="text-xs bg-slate-600/30 text-slate-400 px-2 py-1 rounded">
              Read Only
            </span>
          )}
        </div>

        {renderItemSection(
          sectionName,
          "insights",
          "💡 Key Insights/Ideas/Plans",
          "pink",
          "Enter your insight..."
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <div className="h-10 w-1 bg-gradient-to-r from-pink-500 to-pink-600 rounded-full" />
        <h2 className="text-2xl font-bold text-white">
          Analysis Notes & Insights
          {isPublicView && (
            <span className="ml-3 text-sm bg-blue-600/20 text-blue-300 px-2 py-1 rounded">
              Public View - Read Only
            </span>
          )}
        </h2>
      </div>

      <div className="bg-gradient-to-r from-slate-800/70 to-slate-900/70 rounded-xl p-6 border border-slate-700/50">
        <div className="flex items-start gap-4">
          <div className="text-4xl">📝</div>
          <div>
            <h3 className="text-xl font-bold text-white mb-2">
              {isPublicView ? "View Analysis Notes" : "Document Your Analysis"}
            </h3>
            <p className="text-slate-300 text-sm">
              {isPublicView
                ? "View insights and key takeaways from each section of the recovery capital analysis."
                : 'Use this space to record your insights and key takeaways for each section of your recovery capital analysis. Click on any item to edit it, or use the "Add" buttons to create new entries.'}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {Object.keys(notes)
          .filter((sectionName) => sectionName !== "Additional Thoughts")
          .map((sectionName) => renderSection(sectionName))}
      </div>

      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <FileText className="text-purple-400" size={24} />
          </div>
          <h3 className="text-xl font-bold text-white">Additional Thoughts</h3>
          {isPublicView && (
            <span className="text-xs bg-slate-600/30 text-slate-400 px-2 py-1 rounded">
              Read Only
            </span>
          )}
        </div>

        {renderItemSection(
          "Additional Thoughts",
          "insights",
          "💭 General Insights",
          "purple",
          "Share any additional thoughts..."
        )}
      </div>

      <div className="bg-gradient-to-r from-slate-800/70 to-slate-900/70 rounded-xl p-6 border border-slate-700/50">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="text-2xl font-bold text-white mb-3">
            Comprehensive Analysis Framework
          </h3>
          <p className="text-slate-300">
            {isPublicView
              ? "These documented insights across all analysis sections provide a comprehensive understanding of recovery patterns within the organization."
              : "Your documented insights across all analysis sections create a comprehensive understanding of recovery patterns within your organization. Click on any item to edit it, and use these notes to inform treatment approaches, resource allocation, and strategic planning for improved recovery outcomes."}
          </p>
        </div>
      </div>
    </div>
  );
};

export default RCNotesTab;
